import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Network,
  Database,
  Layers,
  ShieldCheck,
  Zap,
  Calendar,
  GitMerge,
  Globe,
  FileText,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const CALENDLY = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';

const EZCLOUD_TO_SAP = [
  { label: 'MIRO-ready invoices (FI-AP)', note: 'With cost center, profit center, WBS, and internal-order assignment' },
  { label: '3-way match results (MM)', note: 'PO, GR/IR, and invoice matched with tolerance handling' },
  { label: 'Approval metadata + audit trail', note: 'Every approver captured for SOX / statutory audit' },
  { label: 'Linked invoice images (DMS)', note: 'Attached via SAP Document Management' },
  { label: 'Tax + withholding codes', note: 'Country-specific, VAT, GST, withholding tax' },
];

const SAP_TO_EZCLOUD = [
  'Vendor master (LFA1 / LFB1 / LFBK)',
  'Chart of accounts + cost centers + profit centers',
  'Open purchase orders + goods receipts (EKKO / EKPO / MSEG)',
  'Company code + plant hierarchy',
  'WBS elements, internal orders',
  'Tax jurisdictions and tax codes',
];

const DAY_ONE = [
  {
    icon: GitMerge,
    title: 'MIRO-replacement for invoice entry',
    body: "Eliminate manual MIRO keying. EZ Cloud captures, codes, matches (3-way via MM), and posts directly to FI-AP — ready for payment runs in F110.",
  },
  {
    icon: Globe,
    title: 'Multi-company-code, multi-country',
    body: "Global enterprises with dozens of company codes across regions get single-platform AP with local tax compliance and statutory-aware audit trails.",
  },
  {
    icon: Layers,
    title: 'Full FI/CO coding',
    body: 'Cost center, profit center, WBS, internal order, functional area — auto-coded from rules, with project-manager approval routing for WBS-coded invoices.',
  },
  {
    icon: ShieldCheck,
    title: 'Audit-ready for SOX and statutory audits',
    body: 'Full AP audit trail exportable in Big 4-friendly formats. SAP GRC-compatible role separation. SOC 2 compliant environment.',
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud work with SAP ECC 6.0 and S/4HANA?',
    a: 'Both. For ECC, integration uses RFC + BAPI with optional gateway/OData layer. For S/4HANA (on-prem or Cloud), standard OData APIs are used. Same EZ Cloud user experience across both; integration patterns differ under the hood.',
  },
  {
    q: "Does this replace SAP MIRO?",
    a: "Functionally yes — EZ Cloud eliminates the need for AP clerks to sit in MIRO all day. Invoices are captured, coded, matched, and posted to FI-AP without anyone opening MIRO. For specialist cases (manual parking, complex reversals), MIRO remains available in SAP.",
  },
  {
    q: 'How does 3-way match work with SAP MM?',
    a: "EZ Cloud reads open POs from EKKO/EKPO and goods receipts from MSEG. On match, we post the invoice with references so SAP's standard GR/IR and price-variance logic apply. For mismatches, tolerance rules determine whether to auto-post, route to AP, or block.",
  },
  {
    q: 'Does EZ Cloud handle withholding tax and VAT?',
    a: 'Yes. Tax codes and withholding configurations are read from SAP and applied per supplier + jurisdiction. EZ Cloud posts invoices with correct tax codes so SAP handles VAT reporting and withholding exactly as it does today.',
  },
  {
    q: 'What about countries with e-invoicing mandates (Italy, India, Mexico)?',
    a: "EZ Cloud captures the e-invoice, parses structured data (XML / Peppol / SAF-T), and posts to SAP with the compliance data preserved. For outbound e-invoicing (customer invoices), you'd use SAP Document Compliance or a partner; EZ Cloud is AP-side only.",
  },
  {
    q: 'How long is a typical SAP implementation?',
    a: '10-12 weeks for ECC (BASIS involvement, security roles, RFC setup); 8-12 weeks for S/4HANA. Critical-path items are authorization roles, SAP ITSM ticketing, and cutover sandbox testing with a representative invoice sample.',
  },
  {
    q: 'Is EZ Cloud on the SAP Store?',
    a: 'Contact sales for current SAP Store / certified status. Deployment does not require certification — the integration uses standard SAP interfaces.',
  },
];

const SAPPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [roiInputs, setRoiInputs] = useState({ invoices: 8000, costPerInvoice: 22, staffRate: 42 });
  const annualSavings = useMemo(() => {
    const minutesSavedPerInvoice = 14;
    const hoursSaved = (roiInputs.invoices * minutesSavedPerInvoice) / 60;
    const laborMonthly = hoursSaved * roiInputs.staffRate;
    const processingMonthly = roiInputs.invoices * roiInputs.costPerInvoice * 0.55;
    return Math.max(0, (laborMonthly + processingMonthly) * 12);
  }, [roiInputs]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="SAP AP Automation | ECC + S/4HANA MIRO Replacement — EZ Cloud"
        description="Native SAP AP automation for ECC 6.0 and S/4HANA. MIRO replacement, 3-way match via MM, multi-company-code, FI/CO coding, statutory-audit trail."
        canonical="https://ezcloud.ai/integrations/sap"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
          { name: 'SAP', url: 'https://ezcloud.ai/integrations/sap' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <section className="bg-hero-gradient text-white pt-16 pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="relativecontainer mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <Network className="w-4 h-4" aria-hidden />
                Integration · SAP ECC + S/4HANA
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Replace MIRO. Keep SAP.
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-8">
                Native AP automation for SAP ECC 6.0 and S/4HANA. 3-way match via MM, full FI/CO coding, multi-company-code routing, statutory audit trail — no BAPI you didn't ask for.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                  <Calendar className="mr-2 w-5 h-5" aria-hidden />
                  See SAP integration live
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
                <Button size="lg" variant="outline-on-dark" asChild>
                  <Link to="/technology/native-erp">How our ERP integration works</Link>
                </Button>
              </div>
            </div>

            <Card className="p-6 bg-white/10 border-white/20 backdrop-blur-xl">
              <h2 className="text-white font-semibold mb-4">How the integration works</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-accent/20 rounded-lg border border-accent/30">
                  <div className="font-bold text-white">EZ Cloud</div>
                  <ArrowRight className="w-4 h-4 text-accent mx-auto" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Posts invoices to FI-AP with MM match, CO coding, DMS images, tax</div>
                </div>
                <div className="text-center text-xs text-white/50 py-1">RFC/BAPI (ECC) · OData (S/4HANA) · bi-directional</div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="font-bold text-white">SAP</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 mx-auto rotate-180" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Returns vendor master, COA, POs + GR, company codes, cost centers</div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-4">
                No ABAP on your side. Standard interfaces only.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What flows between EZ Cloud and SAP
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Real-time. No interface tables, no batch jobs, no overnight reconciliation.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">EZ Cloud → SAP</h3>
              </div>
              <ul className="space-y-3">
                {EZCLOUD_TO_SAP.map(({ label, note }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="text-gray-900 font-medium">{label}</div>
                      <div className="text-sm text-gray-500">{note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">SAP → EZ Cloud</h3>
              </div>
              <ul className="space-y-3">
                {SAP_TO_EZCLOUD.map((label) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="text-gray-900 font-medium">{label}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">What you get from day one</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {DAY_ONE.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-gray-600">{body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Typical EZ Cloud + SAP customer</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'Enterprise, SAP ECC 6.0 or S/4HANA',
              'Multi-company-code, multi-country, multi-plant',
              '10,000 – 200,000+ invoices per month across the group',
              'High MM / 3-way-match volume (manufacturing, distribution)',
              'SOX-listed or statutory-audit-heavy',
              'Mid-way through S/4HANA migration — we support both concurrently',
            ].map((x) => (
              <div key={x} className="flex items-start gap-2 text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                <span>{x}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/industries/manufacturing" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors">
              Manufacturing <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
            <Link to="/industries/healthcare" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors">
              Healthcare <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Rough SAP ROI</h2>
              <p className="text-gray-600 mb-6">
                Defaults reflect an enterprise SAP AP team. Tune the inputs or switch to an industry-specific calculator for a closer fit.
              </p>
              <Button variant="outline" asChild>
                <Link to="/resources/roi-calculator">Full ROI calculator</Link>
              </Button>
            </div>
            <Card className="p-6 bg-accent/10 border-accent/30">
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Monthly invoices</label>
                  <input type="number" min={0} value={roiInputs.invoices}
                    onChange={(e) => setRoiInputs({ ...roiInputs, invoices: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cost per invoice ($)</label>
                  <input type="number" min={0} value={roiInputs.costPerInvoice}
                    onChange={(e) => setRoiInputs({ ...roiInputs, costPerInvoice: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">AP staff rate ($/hr)</label>
                  <input type="number" min={0} value={roiInputs.staffRate}
                    onChange={(e) => setRoiInputs({ ...roiInputs, staffRate: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-700 mb-1">Estimated annual savings</div>
                <div className="text-4xl font-bold text-accent">${Math.round(annualSavings).toLocaleString()}</div>
              </div>
              <ROIResultsCapture
                industry="SAP Integration"
                annualSavings={annualSavings}
                inputs={{
                  'Monthly invoices': roiInputs.invoices.toLocaleString(),
                  'Cost per invoice': `$${roiInputs.costPerInvoice}`,
                  'Staff hourly rate': `$${roiInputs.staffRate}`,
                }}
                buttonLabel="Email my SAP ROI report"
              />
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              SAP integration FAQ
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Retire MIRO without retiring SAP</h2>
          <p className="text-lg text-gray-600 mb-8">
            30-minute demo — bring a sample PDF, we'll walk a live 3-way match against your PO data and post the result to SAP.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
              Book an SAP demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/technology/native-erp">
                <FileText className="mr-2 w-5 h-5" aria-hidden />
                How native integration works
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SAPPage;
